/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * PostStreamReviewScreen — floating toolbar shown after an AI session ends.
 *
 * Reuses the AI modal infrastructure (decoration plugin + ModalRegion) to render
 * a post-stream review toolbar with the following affordances:
 *   - Undo / Redo (operate on stored slices, NOT the editor history plugin)
 *   - View changes (toggle a diff overlay for the current segment)
 *   - Ask Rovo (a small prompt input)
 *   - Step through changes (reveals Previous / Next navigation between AI change segments)
 *
 * Only mounted when `useUIState` returns `'postStreamReview'`, which requires
 * both `platform_editor_ai_xstate_migration` and
 * `platform_editor_ai_new_aifc_editor_experience` gates to be ON.
 *
 * File layout (this directory):
 *   - `index.tsx` (this file) — screen component, plugin-state wiring, action handlers
 *   - `AskRovo.tsx` — prompt input pill with inline step-through navigation,
 *     show/hide changes toggle and Undo/Redo controls
 *   - `hooks.ts` — `useOutsideClickClose`, `useHideDiffOnUnmount`, `useSegmentNavigation`
 *   - `icons.tsx` — hoisted icon render functions (stable refs)
 *   - `messages.ts` — i18n message descriptors
 *
 * Note: CSS style objects (`wrapperCSS`, `toolbarCSS`) live inline in this
 * file rather than a separate `styles.ts` — `@compiled/react`'s build-time
 * transform requires `css({})` calls to be co-located with the JSX that
 * consumes them, so a cross-file styles module is not supported.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';

import type { AIPlugin } from '../../../../../aiPluginType';
import { openPostStreamReviewModal } from '../../../../../editor-actions/actions';
import {
	resetSessionState,
	startFreeGenStream,
	updateAIContentUndone,
	updateAllAIContentUndone,
} from '../../../../../pm-plugins/ai-session-context/commands';
import { rovoAgentsPluginKey } from '../../../../../pm-plugins/rovo-agents/plugin-key';
import { useContentComponentContext } from '../../../../hooks/useContentComponentContext';
import { ModalRegionErrorBoundary } from '../../../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { ModalRegion } from '../../../ModalRegion/ModalRegion';
import type { Action } from '../../hooks/useUIState';
import { buildDiffParamsForAllSegments, buildDiffParamsForSegment } from '../diff-helpers';
import { segmentCommitPositions } from '../segmentCommitPositions';

import { AskRovo } from './AskRovo';
import { useHideDiffOnUnmount, useOutsideClickClose, useSegmentNavigation } from './hooks';
/**
 * `display: contents` — the wrapper exists purely to hold the ref used by the
 * outside-click handler and the screen testId, without introducing a box that
 * affects layout. All visible chrome is on `toolbarCSS` further inside.
 */
const wrapperCSS = css({ display: 'contents' });

/**
 * Compact single-row toolbar — see file-level JSDoc for the layout sketch.
 *
 * Width is composed via DS spacing tokens to keep the value tied to the
 * design-system rhythm rather than a raw pixel literal. The `space.*` scale
 * tops out at `space.1000` (80px), so 672px = 8 × space.1000 + space.400.
 */
const toolbarCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	width: `calc(8 * ${token('space.1000')} + ${token('space.400')})`,
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.xxlarge', '16px'),
	boxShadow: token('elevation.shadow.overlay'),
	boxSizing: 'border-box',
});

/**
 * Selects the first usable decoration to anchor the modal to. Mirrors the helper
 * in `Prompt.tsx` so the modal positioning logic stays consistent across screens.
 */
function findModalDecoration(modalDecorationSet?: DecorationSet): Decoration | undefined {
	if (!modalDecorationSet) {
		return undefined;
	}
	return (
		modalDecorationSet
			.find(
				undefined,
				undefined,
				(spec) =>
					spec.key === 'modalMountPointDecoration' || spec.key === 'modalMountPointRangeDecoration',
			)
			.shift() ||
		modalDecorationSet
			.find(undefined, undefined, (spec) => spec.key === 'ai-nodeDecoration')
			.shift()
	);
}

/**
 * PostStreamReviewScreen — see file-level JSDoc for the full feature description.
 */
export function PostStreamReviewScreen({
	api,
	dispatch,
	appearance,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	appearance: EditorAppearance;
	dispatch: React.Dispatch<Action>;
}): JSX.Element | null {
	const {
		editorView,
		wrapperElement,
		endExperience,
		editorPluginAIProvider,
		isInDocumentStreamingEnabled,
	} = useContentComponentContext();

	const {
		lastTriggeredFrom,
		modalMountedTimeStamp,
		modalDecorationSet,
		aiContentPositions,
		currentStreamReviewSegmentIndex,
		configItem,
	} = useSharedPluginStateWithSelector(api, ['aiExperience'], ({ aiExperienceState }) => ({
		lastTriggeredFrom: aiExperienceState?.aiDecorationExperiencePluginState.lastTriggeredFrom,
		modalMountedTimeStamp:
			aiExperienceState?.aiDecorationExperiencePluginState.modalMountedTimeStamp,
		modalDecorationSet: aiExperienceState?.aiDecorationExperiencePluginState.modalDecorationSet,
		aiContentPositions: aiExperienceState?.aiSessionPluginState?.aiContentPositions,
		currentStreamReviewSegmentIndex:
			aiExperienceState?.aiSessionPluginState?.currentStreamReviewSegmentIndex,
		configItem: aiExperienceState?.aiDecorationExperiencePluginState.configItem,
	}));

	// Fallback free-gen prompt input (used when product has no Rovo onSubmit).
	const [textInput, setTextInput] = useState('');
	const textInputRef = useRef(textInput);
	textInputRef.current = textInput;

	// `cancel` is wired into `RovoPromptScreenWithLogic` / `PromptScreen`
	// which can invoke it on benign events (Esc inside the prompt input, click
	// outside the prompt input but still inside the toolbar, etc.). For the
	// Post Stream Review screen we explicitly DO NOT want any of those to tear
	// down the modal — the user is still actively reviewing AI changes and the
	// toolbar must stay anchored until they take an explicit dismiss action.
	const cancel = useCallback(
		(_options?: {
			shouldClearSelectionContext?: boolean;
			shouldIgnoreNextSpaceTrigger?: boolean;
		}) => {
			// no-op — see comment above
		},
		[],
	);

	const submit = useCallback(() => {
		const currentInput = textInputRef.current;
		if (!currentInput?.trim()) {
			return;
		}
		api?.core?.actions.execute(startFreeGenStream(currentInput));
	}, [api?.core?.actions]);

	const onTextChange = useCallback((input: string) => {
		setTextInput(input);
	}, []);

	// Rovo chat publish wiring — identical to PromptSessionScreen because the
	// modal runs the same Rovo / fallback prompt UI underneath.
	const rovoChatAIFeatureContext = configItem?.rovoChatAIFeatureContext;
	const publishChatNew = useCallback(
		(prompt: DocNode) => {
			const publish = rovoAgentsPluginKey.getState(editorView.state)?.publish;
			if (publish) {
				const payload: Parameters<typeof publish>[0] = {
					type: 'chat-new',
					product: editorPluginAIProvider.product,
					data: {
						dialogues: [],
						prompt,
						...(rovoChatAIFeatureContext ? { aiFeatureContext: rovoChatAIFeatureContext } : {}),
					},
					source: 'editor',
				};
				publish(payload);
			}
		},
		[editorView, editorPluginAIProvider.product, rovoChatAIFeatureContext],
	);

	// Mirror Prompt.tsx — decide Rovo vs fallback prompt presentation.
	const hasRovoPrompt = !!editorPluginAIProvider.onSubmit;
	const { PromptEditor } = editorPluginAIProvider;
	const hasFallbackPrompt = !hasRovoPrompt && !!PromptEditor;

	const screenRef = useRef<HTMLDivElement>(null);
	const segmentIndex = currentStreamReviewSegmentIndex ?? 0;
	const coarseSegments = aiContentPositions ?? [];

	const [isStepThroughExpanded, setIsStepThroughExpanded] = useState(false);

	// [CCI-17297] Two review modes:
	//   - AGGREGATE (default, first open): "View changes" / Undo / Redo act on
	//     ALL the AI's changes at once. Operates directly on the coarse
	//     `aiContentPositions` — no segmentation needed.
	//   - PER-SEGMENT (step-through): the coarse entries are lazily split into
	//     fine per-node / per-list-item entries via `segmentCommitPositions`, and
	//     the toolbar steps through them one at a time.
	// The mode is driven by the existing step-through toggle so no new control is
	// introduced: expanded ⇒ per-segment, collapsed ⇒ aggregate.
	const isAggregate = !isStepThroughExpanded;

	// Lazily compute fine segments only when stepping through. Each coarse entry
	// expands into one or more fine entries; a flat list is what the toolbar
	// navigates. Falls back to the coarse list if the editor view is unavailable.
	const fineSegments = useMemo(() => {
		if (isAggregate || !editorView) {
			return coarseSegments;
		}
		return coarseSegments.flatMap((entry) => segmentCommitPositions(editorView, entry));
		// `coarseSegments` identity changes whenever aiContentPositions changes,
		// which is the correct trigger for recomputation.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAggregate, editorView, coarseSegments]);

	const segments = isAggregate ? coarseSegments : fineSegments;
	const currentSegment = segments[segmentIndex];
	const totalSegments = segments.length;

	// Undo/Redo state is **derived** from per-segment `isUndone` flag stored on
	// `aiContentPositions[segmentIndex]` in `aiSessionContextPlugin`. This way
	// the correct button state is restored when the user navigates away
	// (Prev/Next) and returns to a segment they previously undid/redid.
	//
	//   - segment.isUndone === true  → only Redo is enabled (user has undone)
	//   - segment.isUndone !== true  → only Undo is enabled (initial state, or user has redone)
	const canUndo = !!currentSegment && !currentSegment.isUndone;
	const canRedo = !!currentSegment?.isUndone;

	const onToggleStepThrough = useCallback(() => {
		setIsStepThroughExpanded((current) => {
			const next = !current;
			if (!editorView || editorView.isDestroyed) {
				return next;
			}
			if (next) {
				// [CCI-17297] Entering step-through (per-segment) mode: reset to the
				// first fine segment so navigation starts at the beginning and the
				// stored `segmentIndex` can't point past the fine-segment list.
				openPostStreamReviewModal({
					editorView,
					segmentData: { segmentIndex: 0 },
				});
			} else {
				// [CCI-17297] Returning to AGGREGATE mode: re-anchor below the LAST
				// segment and re-span the highlight across ALL segments, matching the
				// initial aggregate open.
				const aggregateStart = coarseSegments.length
					? Math.min(...coarseSegments.map((s) => s.startPos))
					: 0;
				const aggregateEnd = coarseSegments.length
					? Math.max(...coarseSegments.map((s) => s.endPos))
					: 0;
				openPostStreamReviewModal({
					editorView,
					segmentData: {
						segmentIndex: Math.max(0, coarseSegments.length - 1),
						positions: { startPos: aggregateStart, endPos: aggregateEnd },
					},
				});
			}
			return next;
		});
	}, [editorView, coarseSegments]);

	const close = useCallback(() => {
		endExperience({ shouldClearSelectionContext: true });
		api?.core?.actions.execute(resetSessionState());
		dispatch({ type: 'terminate' });
	}, [endExperience, api, dispatch]);

	useOutsideClickClose(screenRef, close);

	const onBeforeSubmitForRovoChat = useCallback(
		(prompt: DocNode): boolean => {
			publishChatNew(prompt);
			// Use `close()` (not `cancel()`) because `cancel` is intentionally
			// a no-op in this screen — `close()` is the only path that
			// actually tears down the modal.
			close();
			return true;
		},
		[publishChatNew, close],
	);

	const openInRovoChatConfig = useMemo(
		() => ({ onBeforeSubmit: onBeforeSubmitForRovoChat }),
		[onBeforeSubmitForRovoChat],
	);

	/**
	 * Apply a slice to the current segment's positions, then re-issue the Post
	 * Stream Review modal so its decoration is freshly anchored at the new
	 * `[newStartPos, newEndPos]` range — all in a SINGLE transaction.
	 *
	 * Why a single transaction:
	 *   - `openPostStreamReviewModal` is driven by `createOpenAIModalCommand`,
	 *     which uses `createCommand` and produces ONE transaction.
	 *   - The slice-replace + `UPDATE_AI_CONTENT_POSITION` meta need to be
	 *     visible to that command's transform callback (so the session-context
	 *     plugin's `apply()` sees the new range and
	 *     `aiContentPositions[segmentIndex]` before the decoration plugin maps
	 *     `modalDecorationSet`).
	 *   - We pass them as `beforeTransform`, which runs at the start of the
	 *     command's transform. That way the slice-replace lands in the same
	 *     `tr` as the `SHOW_MODAL` action — no two-dispatch races, no flicker,
	 *     no risk of `modalDecorationSet` collapsing between dispatches.
	 */
	const replaceCurrentSegmentWith = useCallback(
		(direction: 'undo' | 'redo') => {
			// [CCI-17297] Aggregate mode applies every coarse segment and does NOT
			// depend on `currentSegment` (which may be undefined if `segmentIndex`
			// is stale). Only per-segment mode requires a valid `currentSegment`.
			if (!editorView || (!isAggregate && !currentSegment)) {
				return;
			}

			// [CCI-17297] Aggregate mode: apply EVERY coarse segment's slice in one
			// transaction. We process the segments back-to-front (descending
			// startPos) so each replace doesn't shift the positions of the ones not
			// yet processed. Undo applies `originalUserContentSlice`, redo applies
			// `newSlice`. Only segments in the opposite state are touched (undo skips
			// already-undone, redo skips already-applied) to keep ranges valid.
			if (isAggregate) {
				const targets = [...coarseSegments]
					.map((seg, index) => ({ seg, index }))
					.filter(({ seg }) => (direction === 'undo' ? !seg.isUndone : seg.isUndone))
					.sort((a, b) => b.seg.startPos - a.seg.startPos);

				// [CCI-17297] Keep the modal anchored below the LAST segment and the
				// highlight spanning ALL segments after the undo/redo, mirroring the
				// initial aggregate open. We use the pre-transform positions as the
				// anchor; the decoration plugin maps them through `beforeTransform`.
				const aggregateStart = coarseSegments.length
					? Math.min(...coarseSegments.map((s) => s.startPos))
					: 0;
				const aggregateEnd = coarseSegments.length
					? Math.max(...coarseSegments.map((s) => s.endPos))
					: 0;

				openPostStreamReviewModal({
					editorView,
					segmentData: {
						segmentIndex,
						positions: { startPos: aggregateStart, endPos: aggregateEnd },
					},
					beforeTransform: (tr) => {
						for (const { seg } of targets) {
							const sliceToApply =
								direction === 'undo' ? seg.originalUserContentSlice : seg.newSlice;
							try {
								tr.replaceRange(seg.startPos, seg.endPos, sliceToApply);
							} catch {
								// Skip a segment whose range is no longer valid.
							}
						}
						return tr;
					},
				});

				if (!editorView.isDestroyed) {
					api?.core?.actions.execute(updateAllAIContentUndone(direction === 'undo'));
				}
				return;
			}

			const sliceToApply =
				direction === 'undo' ? currentSegment.originalUserContentSlice : currentSegment.newSlice;
			const { startPos, endPos } = currentSegment;

			openPostStreamReviewModal({
				editorView,
				segmentData: { segmentIndex, positions: { startPos, endPos } },
				beforeTransform: (tr) => {
					try {
						tr.replaceRange(startPos, endPos, sliceToApply);
					} catch {
						// If the range is no longer valid (e.g. collaborator deleted it),
						// fall back to just re-opening the modal at the stored positions
						// — nothing to undo / redo.
					}
					return tr;
				},
			});

			// TODO: CCI-16870 - make UPDATE_AI_CONTENT_UNDONE atomic with
			// SHOW_MODAL. This dispatch fires in a second, separate transaction
			// after `openPostStreamReviewModal` above, leaving a one-render
			// window where the decoration has updated but `isUndone` has not
			// yet flipped — `canUndo` / `canRedo` briefly show the wrong
			// enabled/disabled state. The proper fix is to thread an
			// `aiContentUndoneUpdate` meta through `openPostStreamReviewModal`
			// → `createOpenAIModalCommand` and stamp it inside the same
			// `beforeTransform` tr that does the slice replace. Deferring for
			// the spike; the window is sub-frame and not user-perceivable in
			// practice.
			// Guard against the edge case where `editorView` was destroyed
			// between the `openPostStreamReviewModal` dispatch above and this
			// follow-up dispatch (e.g. the user unmounted the editor mid-click).
			if (!editorView.isDestroyed) {
				api?.core?.actions.execute(updateAIContentUndone(segmentIndex, direction === 'undo'));
			}
		},
		[editorView, currentSegment, segmentIndex, api?.core?.actions, isAggregate, coarseSegments],
	);

	// Local diff-visibility state — intentionally NOT derived from
	// `showDiffState.isDisplayingChanges`. We own the lifecycle of the diff
	// for this toolbar and want it to RESET when the user navigates between
	// segments (Prev/Next), which is trivial with local state.
	const [isDisplayingChanges, setIsDisplayingChanges] = useState(false);
	// [CCI-17297] In aggregate mode "View changes" is enabled while any segment is
	// still applied; per-segment mode mirrors `canUndo` for the current segment.
	const canViewChanges = canUndo;

	// Pull the showDiff command bag into locals so the `useCallback` deps array
	// can reference the parent objects instead of destructured leaves (ESLint's
	// `exhaustive-deps` treats `api.showDiff.commands.hideDiff` as depending on
	// the whole `api.showDiff.commands` object).
	const showDiffCommands = api?.showDiff?.commands;
	const coreActions = api?.core?.actions;

	// Tear down the diff overlay before an undo/redo. Without this, the
	// previously-rendered decorations remain painted over content that no
	// longer matches them: undoing flips `canUndo` to false, which also
	// disables the "View Changes" toggle (`canViewChanges = canUndo`), so the
	// user has no way to clear the stale overlay themselves. We hide eagerly
	// here rather than letting the user be stuck with a frozen diff.
	//
	// `isDisplayingChanges` is read through a ref rather than via the
	// `useCallback` dep array so the callback identity stays stable across
	// diff-visibility toggles. Otherwise every View Changes click would
	// recreate `hideDiffIfDisplayed`, which would in turn recreate
	// `onUndo`/`onRedo` and re-render the toolbar's child buttons.
	const isDisplayingChangesRef = useRef(isDisplayingChanges);
	isDisplayingChangesRef.current = isDisplayingChanges;

	const hideDiffIfDisplayed = useCallback(() => {
		if (isDisplayingChangesRef.current && showDiffCommands?.hideDiff && coreActions) {
			coreActions.execute(showDiffCommands.hideDiff);
			setIsDisplayingChanges(false);
		}
	}, [showDiffCommands, coreActions]);

	const onUndo = useCallback(() => {
		hideDiffIfDisplayed();
		replaceCurrentSegmentWith('undo');
	}, [hideDiffIfDisplayed, replaceCurrentSegmentWith]);
	const onRedo = useCallback(() => {
		hideDiffIfDisplayed();
		replaceCurrentSegmentWith('redo');
	}, [hideDiffIfDisplayed, replaceCurrentSegmentWith]);

	const onViewChanges = useCallback(() => {
		// [CCI-17297] Aggregate mode diffs ALL coarse segments and does NOT depend
		// on `currentSegment` (which may be undefined if `segmentIndex` is stale).
		// Only per-segment mode requires a valid `currentSegment`.
		if (
			!editorView ||
			(!isAggregate && !currentSegment) ||
			!showDiffCommands?.hideDiff ||
			!showDiffCommands?.showDiff
		) {
			return;
		}
		if (isDisplayingChanges) {
			coreActions?.execute(showDiffCommands.hideDiff);
			setIsDisplayingChanges(false);
			return;
		}
		// [CCI-17297] Aggregate mode diffs ALL segments at once; per-segment mode
		// diffs only the current segment.
		const result = isAggregate
			? buildDiffParamsForAllSegments(editorView, coarseSegments)
			: buildDiffParamsForSegment(editorView, currentSegment);
		if (!result?.steps || result.steps.length === 0 || !result.originalDoc) {
			return;
		}
		coreActions?.execute(
			showDiffCommands.showDiff({
				originalDoc: result.originalDoc,
				steps: result.steps,
				scrollIntoView: true,
				isInverted: false,
				// [CCI-17297] Render the inline add/remove indicators in the diff
				// overlay. The show-diff plugin internally gates actual rendering
				// behind `platform_editor_diff_plugin_extended`, so passing `true`
				// unconditionally is safe (no-op when that gate is off).
				showIndicators: true,
			}),
		);
		setIsDisplayingChanges(true);
	}, [
		editorView,
		currentSegment,
		isDisplayingChanges,
		coreActions,
		showDiffCommands,
		isAggregate,
		coarseSegments,
	]);

	// Reset per-segment transient state when the user navigates between segments
	// via Prev/Next. Inlined here (rather than extracted into a custom hook) to
	// avoid passing `useCallback` results as inline hook arguments, which would
	// violate the Rules of Hooks (react-hooks/rules-of-hooks).
	useEffect(() => {
		setIsDisplayingChanges(false);
		// Note: `isStepThroughExpanded` is intentionally NOT reset here. Navigating
		// between segments via the Prev/Next chevrons changes `segmentIndex`, and
		// the step-through tray must stay open while the user steps through changes.
		// It is only toggled explicitly via the step-through button.
		if (coreActions && showDiffCommands?.hideDiff) {
			coreActions.execute(showDiffCommands.hideDiff as never);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [segmentIndex]);

	// [CCI-17297] §3.7 — A "remove" segment has no visible content of its own, so
	// when it becomes the current per-segment entry we auto-show the diff overlay
	// (which renders the removed content as a deletion) instead of leaving a blank
	// selection. This also covers the "first node is removed and the user clicks
	// Step through" case: entering per-segment mode lands on the first segment and,
	// if it's a removal, the diff is shown by default.
	//
	// `onViewChanges` toggles, so we only call it when the diff is not already
	// shown. Read through refs to keep the effect from re-firing on every toggle.
	const onViewChangesRef = useRef(onViewChanges);
	onViewChangesRef.current = onViewChanges;
	useEffect(() => {
		if (isAggregate) {
			return;
		}
		if (currentSegment?.kind === 'remove' && !isDisplayingChangesRef.current && canViewChanges) {
			// This is an intentional, navigation-driven side effect: when the active
			// per-segment entry is a removal it has no visible content, so we reveal
			// the diff overlay. It cannot be derived during render because it is a
			// one-shot response to the segment changing, not derived state.
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			onViewChangesRef.current();
		}
		// Re-evaluate whenever the active per-segment entry changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [segmentIndex, isAggregate, currentSegment?.kind, canViewChanges]);

	useHideDiffOnUnmount(coreActions, showDiffCommands?.hideDiff);

	const { goToPrevious, goToNext, canGoPrevious, canGoNext } = useSegmentNavigation(
		editorView,
		segmentIndex,
		totalSegments,
	);

	const decoration = findModalDecoration(modalDecorationSet);
	if (!decoration || !modalDecorationSet) {
		return null;
	}

	return (
		<div ref={screenRef} css={wrapperCSS} data-testid="post-stream-review-session-screen">
			<ModalRegionErrorBoundary inContentComponent onCloseFallback={endExperience}>
				<ModalRegion
					lastTriggeredFrom={lastTriggeredFrom}
					key={modalMountedTimeStamp}
					decoration={decoration}
					editorView={editorView}
					editorRelativeWrapper={wrapperElement?.offsetParent}
					endExperience={endExperience}
					appearance={appearance}
					decorationSet={modalDecorationSet}
					isDirectionalPinningEnabled
				>
					<div css={toolbarCSS}>
						<AskRovo
							hasRovoPrompt={hasRovoPrompt}
							hasFallbackPrompt={hasFallbackPrompt}
							openInRovoChat={configItem?.openInRovoChat}
							openInRovoChatConfig={openInRovoChatConfig}
							cancel={cancel}
							PromptEditor={PromptEditor}
							textInput={textInput}
							onTextChange={onTextChange}
							submit={submit}
							appearance={appearance}
							isAIFC={isInDocumentStreamingEnabled}
							canUndo={canUndo}
							canRedo={canRedo}
							onUndo={onUndo}
							onRedo={onRedo}
							canViewChanges={canViewChanges}
							isDisplayingChanges={isDisplayingChanges}
							onViewChanges={onViewChanges}
							totalSegments={totalSegments}
							segmentIndex={segmentIndex}
							canGoPrevious={canGoPrevious}
							canGoNext={canGoNext}
							onPrevious={goToPrevious}
							onNext={goToNext}
							isStepThroughExpanded={isStepThroughExpanded}
							onToggleStepThrough={onToggleStepThrough}
						/>
					</div>
				</ModalRegion>
			</ModalRegionErrorBoundary>
		</div>
	);
}
